import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Tab,
  TabList,
  Text,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  DismissRegular,
  DocumentPdfRegular,
  EditRegular,
  PlayRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useSession } from '../auth/SessionProvider';
import { PageHeader } from '../components/PageHeader';
import type {
  AssessmentResultDto,
  AssessmentRisk,
  AssessmentRunDetailDto,
  AssessmentRunSummaryDto,
  AssessmentStatus,
} from '../api/types';
import './assessment.css';

const STATUS_META: Record<AssessmentStatus, { label: string; bg: string; fg: string; color: string; rank: number }> = {
  Fail: { label: 'Fail', bg: '#fde7e9', fg: '#b3261e', color: '#d13438', rank: 0 },
  Warn: { label: 'Warn', bg: '#fdf3d8', fg: '#8a5a00', color: '#e0a100', rank: 1 },
  NeedsPermission: { label: 'Berechtigung', bg: '#f0eafa', fg: '#6b4ea8', color: '#8764b8', rank: 2 },
  Error: { label: 'Fehler', bg: '#fde7e9', fg: '#8a1c1c', color: '#a4262c', rank: 3 },
  ManualOpen: { label: 'offen', bg: '#eef0f0', fg: '#5a5a5a', color: '#9aa0a0', rank: 4 },
  Info: { label: 'Info', bg: '#e8f0fe', fg: '#2a5bb8', color: '#5b8def', rank: 5 },
  NotApplicable: { label: 'n.a.', bg: '#f0f0f0', fg: '#8a8a8a', color: '#cccccc', rank: 6 },
  Pass: { label: 'Pass', bg: '#e6f4ea', fg: '#1e7d34', color: '#3a9b58', rank: 7 },
};

const RISK_RANK: Record<string, number> = { Hoch: 0, Mittel: 1, Niedrig: 2 };
const MANUAL_OPTIONS: AssessmentStatus[] = ['Pass', 'Warn', 'Fail', 'NotApplicable', 'ManualOpen'];

type SortCol = 'order' | 'id' | 'title' | 'risk' | 'status';

export function AssessmentPage() {
  const api = useApi();
  const { me } = useSession();
  const isAdmin = me?.isAdmin ?? false;
  const [runs, setRuns] = useState<AssessmentRunSummaryDto[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AssessmentRunDetailDto | null>(null);
  const [catalogCount, setCatalogCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [exporting, setExporting] = useState<null | 'xlsx' | 'pdf'>(null);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<AssessmentResultDto | null>(null);
  const [editExpected, setEditExpected] = useState('');
  const [editRisk, setEditRisk] = useState<AssessmentRisk>('Mittel');
  const [editSaving, setEditSaving] = useState(false);
  const [tab, setTab] = useState<string>('Gesamt');
  const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({ col: 'order', dir: 'asc' });
  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<Set<AssessmentStatus>>(new Set());

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rs = await api.getAssessmentRuns();
        if (!active) return;
        setRuns(rs);
        if (rs.length > 0) {
          const d = await api.getAssessmentRun(rs[0].id);
          if (!active) return;
          setSelectedRunId(rs[0].id);
          setDetail(d);
        } else {
          const cat = await api.getAssessmentCatalog();
          if (active) setCatalogCount(cat.length);
        }
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [api]);

  async function start() {
    setStarting(true);
    setError(null);
    try {
      const d = await api.startAssessment();
      setDetail(d);
      setSelectedRunId(d.run.id);
      setRuns(await api.getAssessmentRuns());
      setTab('Gesamt');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setStarting(false);
    }
  }

  async function download(kind: 'xlsx' | 'pdf') {
    if (!selectedRunId) return;
    setExporting(kind);
    setError(null);
    try {
      const { blob, filename } =
        kind === 'pdf' ? await api.exportAssessmentRunPdf(selectedRunId) : await api.exportAssessmentRun(selectedRunId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(null);
    }
  }

  function openEdit(r: AssessmentResultDto) {
    setEditTarget(r);
    setEditExpected(r.expected);
    setEditRisk(r.risk);
  }

  async function saveEdit() {
    if (!editTarget) return;
    setEditSaving(true);
    setError(null);
    try {
      await api.updateAssessmentCheck(editTarget.checkId, { expected: editExpected, risk: editRisk });
      setEditTarget(null);
      // Lauf neu laden, damit die geänderten Soll-Werte/Risiken in der Tabelle erscheinen.
      if (selectedRunId) setDetail(await api.getAssessmentRun(selectedRunId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setEditSaving(false);
    }
  }

  async function selectRun(id: string) {
    setSelectedRunId(id);
    try {
      setDetail(await api.getAssessmentRun(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function rate(checkId: string, status: AssessmentStatus) {
    if (!selectedRunId) return;
    try {
      const d = await api.setAssessmentManual(selectedRunId, checkId, { status });
      setDetail(d);
      setRuns((rs) => rs.map((r) => (r.id === d.run.id ? d.run : r)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const results = detail?.results ?? [];
  const categories = useMemo(() => [...new Set(results.map((r) => r.category))], [results]);

  const tabTotal = tab === 'Gesamt' ? results.length : results.filter((r) => r.category === tab).length;
  const filtersActive = search.trim() !== '' || riskFilter.size > 0 || statusFilter.size > 0;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = results
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => tab === 'Gesamt' || r.category === tab)
      .filter(({ r }) => riskFilter.size === 0 || riskFilter.has(r.risk))
      .filter(({ r }) => statusFilter.size === 0 || statusFilter.has(r.status))
      .filter(({ r }) =>
        q === '' ||
        [r.checkId, r.title, r.what, r.expected, r.actualValue, r.note, r.cis, r.scope, r.category, r.risk, STATUS_META[r.status].label]
          .some((f) => (f ?? '').toLowerCase().includes(q)),
      );
    const cmp = (a: { r: AssessmentResultDto; i: number }, b: { r: AssessmentResultDto; i: number }) => {
      let v = 0;
      if (sort.col === 'order') v = a.i - b.i;
      else if (sort.col === 'id') v = a.r.checkId.localeCompare(b.r.checkId, undefined, { numeric: true });
      else if (sort.col === 'title') v = a.r.title.localeCompare(b.r.title);
      else if (sort.col === 'risk') v = (RISK_RANK[a.r.risk] ?? 9) - (RISK_RANK[b.r.risk] ?? 9);
      else if (sort.col === 'status') v = STATUS_META[a.r.status].rank - STATUS_META[b.r.status].rank;
      return sort.dir === 'asc' ? v : -v;
    };
    return rows.sort(cmp).map(({ r }) => r);
  }, [results, tab, sort, search, riskFilter, statusFilter]);

  function toggleSort(col: SortCol) {
    setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
  }

  function toggleRisk(rk: string) {
    setRiskFilter((s) => {
      const n = new Set(s);
      if (n.has(rk)) n.delete(rk);
      else n.add(rk);
      return n;
    });
  }

  function resetFilters() {
    setSearch('');
    setRiskFilter(new Set());
    setStatusFilter(new Set());
  }

  const header = <PageHeader title="M365-Assessment" description="Tenant gegen die Governance-Prüfmatrix (CIS M365 v7, DSGVO, ISO) prüfen. Automatische Graph-Checks plus manuelle Bewertung; jeder Lauf wird gespeichert." />;

  if (loading) {
    return (
      <div>
        {header}
        <Spinner label="Lade Assessment…" />
      </div>
    );
  }

  return (
    <div>
      {header}
      <div className="asm-stack">
        <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'center', flexWrap: 'wrap' }}>
          {isAdmin ? (
            <Button appearance="primary" icon={starting ? <Spinner size="tiny" /> : <PlayRegular />} disabled={starting} onClick={start}>
              {starting ? 'Assessment läuft…' : 'Assessment starten'}
            </Button>
          ) : null}
          {runs.length > 0 ? (
            <Dropdown
              style={{ minWidth: '260px' }}
              value={runLabel(runs.find((r) => r.id === selectedRunId))}
              selectedOptions={selectedRunId ? [selectedRunId] : []}
              onOptionSelect={(_, d) => d.optionValue && selectRun(d.optionValue)}
            >
              {runs.map((r) => (
                <Option key={r.id} value={r.id} text={runLabel(r)}>
                  {runLabel(r)}
                </Option>
              ))}
            </Dropdown>
          ) : null}
          {detail ? (
            <>
              <Button
                appearance="secondary"
                icon={exporting === 'xlsx' ? <Spinner size="tiny" /> : <ArrowDownloadRegular />}
                disabled={exporting !== null}
                onClick={() => download('xlsx')}
              >
                Export (Excel)
              </Button>
              <Button
                appearance="secondary"
                icon={exporting === 'pdf' ? <Spinner size="tiny" /> : <DocumentPdfRegular />}
                disabled={exporting !== null}
                onClick={() => download('pdf')}
              >
                Export (PDF)
              </Button>
            </>
          ) : null}
          {detail ? (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              {runs.length} gespeicherte{runs.length === 1 ? 'r Lauf' : ' Läufe'}
            </Caption1>
          ) : null}
        </div>

        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}

        {!detail ? (
          <div className="asm-card">
            <Text>
              Noch kein Assessment gelaufen.{' '}
              {catalogCount != null ? `${catalogCount} Prüfpunkte im Katalog bereit.` : ''} Mit „Assessment starten" den
              ersten Durchlauf erzeugen.
            </Text>
          </div>
        ) : (
          <>
            <TopSection run={detail.run} results={results} />

            {runs.length >= 2 ? <Trend runs={runs} selectedId={selectedRunId} onSelect={selectRun} /> : null}

            <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as string)}>
              <Tab value="Gesamt">Gesamt</Tab>
              {categories.map((c) => {
                const fails = results.filter((r) => r.category === c && r.status === 'Fail').length;
                return (
                  <Tab key={c} value={c}>
                    {c}
                    {fails > 0 ? ` (${fails})` : ''}
                  </Tab>
                );
              })}
            </TabList>

            <div className="asm-filterbar">
              <Input
                className="asm-search"
                placeholder="Spalten durchsuchen…"
                value={search}
                onChange={(_, d) => setSearch(d.value)}
                contentBefore={<SearchRegular />}
                contentAfter={
                  search ? (
                    <DismissRegular style={{ cursor: 'pointer' }} onClick={() => setSearch('')} aria-label="Suche leeren" />
                  ) : undefined
                }
              />
              <div className="asm-toggles" role="group" aria-label="Risiko-Filter">
                {(['Hoch', 'Mittel', 'Niedrig'] as const).map((rk) => (
                  <button
                    key={rk}
                    type="button"
                    aria-pressed={riskFilter.has(rk)}
                    className={riskFilter.has(rk) ? 'asm-toggle active' : 'asm-toggle'}
                    onClick={() => toggleRisk(rk)}
                  >
                    {rk}
                  </button>
                ))}
              </div>
              <Dropdown
                multiselect
                style={{ minWidth: '150px' }}
                placeholder="Status: alle"
                value={statusFilter.size ? `Status (${statusFilter.size})` : 'Status: alle'}
                selectedOptions={[...statusFilter]}
                onOptionSelect={(_, d) => setStatusFilter(new Set(d.selectedOptions as AssessmentStatus[]))}
              >
                {(Object.keys(STATUS_META) as AssessmentStatus[]).map((s) => (
                  <Option key={s} value={s} text={STATUS_META[s].label}>
                    {STATUS_META[s].label}
                  </Option>
                ))}
              </Dropdown>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  {visible.length} / {tabTotal}
                </Caption1>
                {filtersActive ? (
                  <Button size="small" appearance="subtle" onClick={resetFilters}>
                    Zurücksetzen
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="asm-tablewrap">
              <table className="asm-table">
                <thead>
                  <tr>
                    <SortHead label="ID" col="id" sort={sort} onSort={toggleSort} />
                    <SortHead label="Prüfpunkt" col="title" sort={sort} onSort={toggleSort} />
                    <SortHead label="Risiko" col="risk" sort={sort} onSort={toggleSort} />
                    <SortHead label="Status" col="status" sort={sort} onSort={toggleSort} />
                    <th className="nosort">Ist-Wert</th>
                    <th className="nosort">Soll-Wert</th>
                    <th className="nosort">CIS</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={r.checkId}>
                      <td className="asm-id">{r.checkId}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Text weight="semibold" size={200}>
                            {r.title}
                          </Text>
                          {isAdmin ? (
                            <Button
                              size="small"
                              appearance="subtle"
                              icon={<EditRegular />}
                              aria-label="Soll-Wert / Risiko bearbeiten"
                              title="Soll-Wert / Risiko bearbeiten"
                              onClick={() => openEdit(r)}
                            />
                          ) : null}
                        </div>
                        <span className="asm-note">{r.what}</span>
                      </td>
                      <td>
                        <RiskBadge risk={r.risk} />
                      </td>
                      <td>
                        {isAdmin && r.source === 'Manual' && r.status !== 'NeedsPermission' ? (
                          <Dropdown
                            size="small"
                            style={{ minWidth: '120px' }}
                            value={STATUS_META[r.status].label}
                            selectedOptions={[r.status]}
                            onOptionSelect={(_, d) => d.optionValue && rate(r.checkId, d.optionValue as AssessmentStatus)}
                          >
                            {MANUAL_OPTIONS.map((s) => (
                              <Option key={s} value={s} text={STATUS_META[s].label}>
                                {STATUS_META[s].label}
                              </Option>
                            ))}
                          </Dropdown>
                        ) : (
                          <StatusChip status={r.status} />
                        )}
                      </td>
                      <td className="asm-actual">
                        {r.actualValue ?? (r.source === 'Manual' ? '—' : '')}
                        {r.note ? <span className="asm-note">{r.note}</span> : null}
                      </td>
                      <td>
                        <Caption1>{r.expected}</Caption1>
                      </td>
                      <td>
                        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{shortCis(r.cis)}</Caption1>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <Dialog open={editTarget !== null} onOpenChange={(_, d) => (d.open ? undefined : setEditTarget(null))}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Prüfpunkt bearbeiten{editTarget ? ` · ${editTarget.checkId}` : ''}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                {editTarget ? (
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{editTarget.title}</Caption1>
                ) : null}
                <Field label="Soll-Wert / Best Practice">
                  <Textarea
                    value={editExpected}
                    onChange={(_, d) => setEditExpected(d.value)}
                    resize="vertical"
                    rows={3}
                  />
                </Field>
                <Field label="Risiko">
                  <Dropdown
                    value={editRisk}
                    selectedOptions={[editRisk]}
                    onOptionSelect={(_, d) => d.optionValue && setEditRisk(d.optionValue as AssessmentRisk)}
                  >
                    {(['Hoch', 'Mittel', 'Niedrig'] as const).map((rk) => (
                      <Option key={rk} value={rk} text={rk}>
                        {rk}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setEditTarget(null)} disabled={editSaving}>
                Abbrechen
              </Button>
              <Button appearance="primary" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? 'Speichere…' : 'Speichern'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

function TopSection({ run, results }: { run: AssessmentRunSummaryDto; results: AssessmentResultDto[] }) {
  const highOpen = results.filter((r) => r.risk === 'Hoch' && (r.status === 'Fail' || r.status === 'Warn')).length;

  const donut = [
    { value: run.pass, color: STATUS_META.Pass.color },
    { value: run.warn, color: STATUS_META.Warn.color },
    { value: run.fail, color: STATUS_META.Fail.color },
    { value: run.needsPermission, color: STATUS_META.NeedsPermission.color },
    { value: run.manualOpen, color: STATUS_META.ManualOpen.color },
    { value: run.info + run.notApplicable + run.error, color: STATUS_META.NotApplicable.color },
  ].filter((s) => s.value > 0);

  return (
    <div className="asm-top">
      <div className="asm-card asm-kpis">
        <div className="asm-score">
          <span className="asm-score-value" style={{ color: scoreColor(run.scorePercent) }}>
            {run.scorePercent}%
          </span>
          <span className="asm-kpi-label">Compliance-Score (Pass / bewertet)</span>
        </div>
        <Kpi value={run.pass} label="Pass" color={STATUS_META.Pass.color} />
        <Kpi value={run.warn} label="Warn" color={STATUS_META.Warn.color} />
        <Kpi value={run.fail} label="Fail" color={STATUS_META.Fail.color} />
        <Kpi value={highOpen} label="offene Hoch-Risiken" color={highOpen > 0 ? STATUS_META.Fail.color : undefined} />
        <Kpi value={run.manualOpen} label="manuell offen" />
        <Kpi value={run.needsPermission} label="Berechtigung fehlt" color={run.needsPermission > 0 ? STATUS_META.NeedsPermission.color : undefined} />
      </div>

      <div className="asm-card asm-charts">
        <Donut segments={donut} centerLabel={`${run.pass}/${run.total}`} centerSub="Pass" />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <CategoryBars results={results} />
        </div>
      </div>
    </div>
  );
}

function Kpi({ value, label, color }: { value: number; label: string; color?: string }) {
  return (
    <div className="asm-kpi">
      <span className="asm-kpi-value" style={color ? { color } : undefined}>
        {value}
      </span>
      <span className="asm-kpi-label">{label}</span>
    </div>
  );
}

function Donut({ segments, centerLabel, centerSub }: { segments: { value: number; color: string }[]; centerLabel: string; centerSub: string }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = 52;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg viewBox="0 0 140 140" width="132" height="132" style={{ flexShrink: 0 }}>
      <g transform="rotate(-90 70 70)">
        {segments.map((s, i) => {
          const len = (s.value / total) * c;
          const el = (
            <circle
              key={i}
              cx="70"
              cy="70"
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth="16"
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
            />
          );
          offset += len;
          return el;
        })}
      </g>
      <text x="70" y="67" textAnchor="middle" fontSize="22" fontWeight="700" fill="currentColor">
        {centerLabel}
      </text>
      <text x="70" y="85" textAnchor="middle" fontSize="11" fill="currentColor" opacity="0.55">
        {centerSub}
      </text>
    </svg>
  );
}

function CategoryBars({ results }: { results: AssessmentResultDto[] }) {
  const rows = useMemo(() => {
    const m = new Map<string, { pass: number; scored: number }>();
    for (const r of results) {
      if (r.status !== 'Pass' && r.status !== 'Warn' && r.status !== 'Fail') continue;
      const e = m.get(r.category) ?? { pass: 0, scored: 0 };
      e.scored += 1;
      if (r.status === 'Pass') e.pass += 1;
      m.set(r.category, e);
    }
    return [...m.entries()].map(([cat, v]) => ({
      cat,
      pct: v.scored ? Math.round((100 * v.pass) / v.scored) : 0,
      scored: v.scored,
    }));
  }, [results]);

  if (rows.length === 0) {
    return <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Keine automatisch bewerteten Prüfpunkte.</Caption1>;
  }

  return (
    <div className="asm-bars">
      {rows.map((row) => (
        <div key={row.cat} className="asm-bar-row">
          <div className="asm-bar-head">
            <span title={row.cat}>{row.cat}</span>
            <span>{row.pct}%</span>
          </div>
          <div className="asm-bar-track">
            <div className="asm-bar-fill" style={{ width: `${row.pct}%`, background: scoreColor(row.pct) }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Trend({
  runs,
  selectedId,
  onSelect,
}: {
  runs: AssessmentRunSummaryDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const data = [...runs].slice(0, 14).reverse();
  const maxH = 40;
  return (
    <div className="asm-card asm-trend">
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Score-Verlauf ({data.length} Läufe)</Caption1>
      <div className="asm-trend-bars">
        {data.map((r) => (
          <button
            key={r.id}
            type="button"
            className={r.id === selectedId ? 'asm-trend-bar sel' : 'asm-trend-bar'}
            title={`${new Date(r.startedUtc).toLocaleString('de-DE')} · ${r.scorePercent}%`}
            onClick={() => onSelect(r.id)}
          >
            <span className="asm-trend-pct">{r.scorePercent}</span>
            <span
              className="asm-trend-fill"
              style={{ height: `${Math.max(3, (r.scorePercent / 100) * maxH)}px`, background: scoreColor(r.scorePercent) }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function SortHead({
  label,
  col,
  sort,
  onSort,
}: {
  label: string;
  col: SortCol;
  sort: { col: SortCol; dir: 'asc' | 'desc' };
  onSort: (c: SortCol) => void;
}) {
  const active = sort.col === col;
  return (
    <th onClick={() => onSort(col)}>
      {label}
      {active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

function StatusChip({ status }: { status: AssessmentStatus }) {
  const m = STATUS_META[status];
  return (
    <span className="asm-chip" style={{ background: m.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}

function RiskBadge({ risk }: { risk: string }) {
  const color = risk === 'Hoch' ? 'danger' : risk === 'Mittel' ? 'warning' : 'informative';
  return (
    <Badge appearance="tint" color={color as 'danger' | 'warning' | 'informative'}>
      {risk}
    </Badge>
  );
}

function scoreColor(pct: number): string {
  if (pct >= 80) return STATUS_META.Pass.color;
  if (pct >= 50) return STATUS_META.Warn.color;
  return STATUS_META.Fail.color;
}

function shortCis(cis?: string): string {
  if (!cis) return '–';
  const dash = cis.indexOf('–');
  return dash > 0 ? cis.slice(0, dash).trim() : cis.length > 14 ? `${cis.slice(0, 14)}…` : cis;
}

function runLabel(r?: AssessmentRunSummaryDto): string {
  if (!r) return '';
  const d = new Date(r.startedUtc);
  return `${d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })} · ${r.scorePercent}%`;
}
